import React, { FC, useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import { useUserIP } from '@services/UserIP';
import { useUserAgent } from '@hooks/useUserAgent';
import { IDataSubjectRightForm } from '@interfaces/DataSubjectRight.interface';
import { useTranslate } from '@translations/useTranslate';
import { IFormItemType } from '@interfaces/FormItemType';
import { SubFormSection } from '@ui/components/molecules/SubFormSection';
import { IMappedDataSubjectRightLookups } from '@interfaces/DataSubjectRightLookups.interface';
import { CustomButton, CustomInputFeedback, FormContainer } from '@ui/components/atoms';
import ArrowIcon from '@assets/svg/arrow.svg';
import { Form } from 'react-bootstrap';
import { getOptionByKey } from '@utils/helper.util';
import { useDataSubjectRightSubmit } from '@services/DataSubjectRight';
import TermsOfUse from '@ui/components/atoms/TermsOfUse';
import { dataSubjectRightFormValidationScheme } from './DataSubjectRightForm.scheme';

export const DataSubjectRightForm: FC<{
  lookups: IMappedDataSubjectRightLookups;
  onRequestSuccess: (
    isEmail: boolean,
    verificationText: string,
    isOwn: boolean,
    requestId: string
  ) => void;
}> = ({ lookups, onRequestSuccess }) => {
  const { t } = useTranslate();
  const userAgent = useUserAgent();
  const recaptchaRef = React.useRef<ReCAPTCHA | undefined>();
  const [error, setError] = React.useState<string | undefined>();
  const [isMyRequest, setIsMyRequest] = React.useState<boolean | undefined>(undefined);
  const [isOther, setIsOther] = React.useState<boolean>(false);
  const [isTawalRelationOther, setIsTawalRelationOther] = React.useState<boolean>(false);
  const [requestType, setRequestType] = React.useState<string | undefined>();
  const [verificationType, setVerificationType] = React.useState<string | undefined>();
  const [recaptcha, setRecaptcha] = React.useState<ReCAPTCHA | undefined>();
  const ipMutation = useUserIP();
  const { mutateAsync: submitRequest, isLoading } = useDataSubjectRightSubmit();

  React.useEffect(() => {
    if (recaptchaRef.current) {
      setRecaptcha(recaptchaRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recaptchaRef.current]);

  const submit = useCallback((values, ip) => {
    const removedEmptyValues: IDataSubjectRightForm = Object.fromEntries(
      Object.entries(values).map(([key, value]) => {
        return [key, value === '' ? undefined : value];
      })
    ) as unknown as IDataSubjectRightForm;

    const mappedValues: IDataSubjectRightForm = {
      ...removedEmptyValues,
      dsrOwnId:
        getOptionByKey(removedEmptyValues.dsrOwnId, lookups.DSR_OWN)?.id ??
        removedEmptyValues.dsrOwnId,
      locationId:
        getOptionByKey(removedEmptyValues.locationId, lookups.LOCATION)?.id ??
        removedEmptyValues.locationId,
      dsrRelationshipId: removedEmptyValues.dsrRelationshipId
        ? getOptionByKey(removedEmptyValues.dsrRelationshipId, lookups.DSR_RELATIONSHIP)?.id ??
          removedEmptyValues.dsrRelationshipId
        : removedEmptyValues.dsrRelationshipId,
      relationshipWithTAWALId: removedEmptyValues.relationshipWithTAWALId
        ? getOptionByKey(
            removedEmptyValues.relationshipWithTAWALId,
            lookups.RELATIONSHIP_WITH_TAWAL
          )?.id ?? removedEmptyValues.relationshipWithTAWALId
        : removedEmptyValues.relationshipWithTAWALId,
      requestTypeId: removedEmptyValues.requestTypeId
        ? getOptionByKey(removedEmptyValues.requestTypeId, lookups.REQUEST_TYPE)?.id ??
          removedEmptyValues.requestTypeId
        : removedEmptyValues.requestTypeId,
      receiveInfoId: removedEmptyValues.receiveInfoId
        ? getOptionByKey(removedEmptyValues.receiveInfoId, lookups.RECEIVE_INFO)?.id ??
          removedEmptyValues.receiveInfoId
        : removedEmptyValues.receiveInfoId,
      userVerificationId: removedEmptyValues.userVerificationId
        ? getOptionByKey(removedEmptyValues.userVerificationId, lookups.USER_VERIFICATION)?.id ??
          removedEmptyValues.userVerificationId
        : removedEmptyValues.userVerificationId,
      nationalityId: removedEmptyValues.nationalityId
        ? getOptionByKey(removedEmptyValues.nationalityId, lookups.DSR_NATIONALITY)?.id ??
          removedEmptyValues.nationalityId
        : removedEmptyValues.nationalityId,
      deviceType: userAgent?.browser.name ?? 'UNKNOWN',
      ipAddress: ip,
    };

    const formData = new FormData();
    Object.entries(mappedValues).forEach(([key, value]) => {
      formData.append(key, value ?? '');
    });
    submitRequest(formData, {
      onError: () => {
        setError(t('global.error'));
      },
    })
      .then((requestId) => {
        onRequestSuccess(
          !!mappedValues.verificationEmail,
          mappedValues.verificationEmail ?? (mappedValues.verificationPhoneNumber as string),
          values.dsrOwnId === 'YES',
          requestId.requestId
        );
      })
      .finally(() => {
        if (recaptcha) {
          recaptcha.reset();
        }
      });
  }, []);

  const dataSubjectRightFormScheme = useMemo(() => dataSubjectRightFormValidationScheme(t), [t]);
  const formik = useFormik<IDataSubjectRightForm>({
    initialValues: {
      dsrOwnId: '',
      name: '',
      email: '',
      phoneNumber: '',
      currentAddress: '',
      locationId: '',
      relationshipWithTAWALId: '',
      otherDsrRelationship: '',
      dsrRelationshipId: '',
      otherRelationshipWithTAWAL: '',
      requestTypeId: '',
      informDetails: '',
      accessDetails: '',
      obtainCopyDetails: '',
      correctionDetails: '',
      destructionDetails: '',
      withdrawConsentDetails: '',
      receiveInfoId: '',
      userVerificationId: '',
      verificationPhoneNumber: '',
      verificationEmail: '',
      nationalityId: '',
      recaptchaToken: '',
      identityFileContent: null,
      ipAddress: '',
      deviceType: '',
      termsOfUse: false,
      privacyNotice: false,
    },
    onSubmit: async (values) => {
      const { ip } = await ipMutation.mutateAsync();
      submit(values, ip);
    },
    validationSchema: dataSubjectRightFormScheme,
    validateOnChange: false,
    validateOnBlur: true,
  });
  const baseFormItems: IFormItemType[] = useMemo(() => {
    return [
      {
        id: 'dsrOwnId',
        label: t('dsr.form.dsrOwn.label'),
        placeholder: t('dsr.form.select.placeholder'),
        type: 'select',
        required: true,
        isValid: true,
        onChange: (value) => {
          formik.resetForm();
          setIsMyRequest(value === 'YES');
          formik.setFieldValue('dsrOwnId', value);
        },
        onBlur: () => formik.setFieldTouched('dsrOwnId', true),
        options: lookups.DSR_OWN,
      },
    ];
  }, [formik, lookups.DSR_OWN, t]);
  const userDetailsFormItems: IFormItemType[] = useMemo(() => {
    const formItems: IFormItemType[] = [
      {
        id: 'name',
        label: t('dsr.form.name.label'),
        placeholder: t('dsr.form.name.placeholder'),
        type: 'text',
        required: true,
        value: formik.values.name,
        isTouched: formik.touched.name,
        isValid: !formik.errors.name,
        errorFeedback: formik.errors.name,
        maxLength: 60,
        onChange: (value) => formik.setFieldValue('name', value),
        onBlur: () => formik.setFieldTouched('name', true),
      },
      {
        id: 'phoneNumber',
        label: t('dsr.form.phone.label'),
        placeholder: '+xxxxxxxxxxx',
        type: 'number',
        required: true,
        value: formik.values.phoneNumber,
        isTouched: formik.touched.phoneNumber,
        isValid: !formik.errors.phoneNumber,
        errorFeedback: formik.errors.phoneNumber,
        maxLength: 15,
        onChange: (value) => formik.setFieldValue('phoneNumber', value),
        onBlur: () => formik.setFieldTouched('phoneNumber', true),
        note: t('dsr.form.phone.note'),
      },
      {
        id: 'email',
        label: t('dsr.form.email.label'),
        placeholder: t('dsr.form.email.placeholder'),
        type: 'text',
        required: true,
        value: formik.values.email,
        isTouched: formik.touched.email,
        isValid: !formik.errors.email,
        errorFeedback: formik.errors.email,
        maxLength: 40,
        onChange: (value) => formik.setFieldValue('email', value),
        onBlur: () => formik.setFieldTouched('email', true),
      },
      {
        id: 'currentAddress',
        label: t('dsr.form.currentAddress.label'),
        placeholder: t('dsr.form.currentAddress.placeholder'),
        type: 'text',
        required: true,
        value: formik.values.currentAddress,
        isTouched: formik.touched.currentAddress,
        isValid: !formik.errors.currentAddress,
        errorFeedback: formik.errors.currentAddress,
        maxLength: 100,
        onChange: (value) => formik.setFieldValue('currentAddress', value),
        onBlur: () => formik.setFieldTouched('currentAddress', true),
      },
    ];
    if (isMyRequest) {
      formItems.push({
        id: 'locationId',
        label: t('dsr.form.location.label'),
        placeholder: t('dsr.form.select.placeholder'),
        type: 'select',
        required: true,
        value: formik.values.locationId,
        isTouched: formik.touched.locationId,
        isValid: !formik.errors.locationId,
        errorFeedback: formik.errors.locationId,
        options: lookups.LOCATION,
        onChange: (value) => formik.setFieldValue('locationId', value),
        onBlur: () => formik.setFieldTouched('locationId', true),
        note: t('dsr.form.location.note'),
      });
    } else {
      formItems.push({
        id: 'dsrRelationshipId',
        label: t('dsr.form.dsrRelationshipId.label'),
        placeholder: t('dsr.form.select.placeholder'),
        type: 'select',
        required: true,
        value: formik.values.dsrRelationshipId,
        isTouched: formik.touched.dsrRelationshipId,
        isValid: !formik.errors.dsrRelationshipId,
        errorFeedback: formik.errors.dsrRelationshipId,
        onChange: (value) => {
          setIsOther(value === 'OTHERS');
          formik.setFieldValue('dsrRelationshipId', value);
        },
        onBlur: () => formik.setFieldTouched('dsrRelationshipId', true),
        options: lookups.DSR_RELATIONSHIP,
      });
      if (isOther) {
        formItems.push({
          id: 'otherDsrRelationship',
          label: t('dsr.form.other.label'),
          placeholder: t('dsr.form.other.label'),
          type: 'text',
          required: true,
          value: formik.values.otherDsrRelationship,
          isTouched: formik.touched.otherDsrRelationship,
          isValid: !formik.errors.otherDsrRelationship,
          errorFeedback: formik.errors.otherDsrRelationship,
          maxLength: 25,
          onChange: (value) => formik.setFieldValue('otherDsrRelationship', value),
          onBlur: () => formik.setFieldTouched('otherDsrRelationship', true),
        });
      }
    }
    formItems.push({
      id: 'nationalityId',
      label: t('dsr.form.nationality.label'),
      placeholder: t('dsr.form.nationality.placeholder'),
      type: 'select',
      required: true,
      value: formik.values.nationalityId,
      isTouched: formik.touched.nationalityId,
      isValid: !formik.errors.nationalityId,
      errorFeedback: formik.errors.nationalityId,
      options: lookups.DSR_NATIONALITY,
      note: t('dsr.form.nationality.note'),
      onChange: (value) => formik.setFieldValue('nationalityId', value),
      onBlur: () => formik.setFieldTouched('nationalityId', true),
    });
    if (formik.values.nationalityId) {
      formItems.push({
        id: 'identityFileContent',
        type: 'attachment',
        required: true,
        value: formik.values.identityFileContent,
        isTouched: formik.touched.identityFileContent,
        isValid: !formik.errors.identityFileContent,
        errorFeedback: formik.errors.identityFileContent,
        nationality: formik.values.nationalityId,
        label: '',
        placeholder: '',
        onChange: (value) => {
          formik.setFieldValue('identityFileContent', value);
          formik.setFieldTouched('identityFileContent', true);
        },
        onBlur: () => formik.setFieldTouched('identityFileContent', true),
      });
    }
    return formItems;
  }, [formik, isMyRequest, isOther, lookups.DSR_RELATIONSHIP, lookups.LOCATION, t]);
  const relationFormItems: IFormItemType[] = useMemo(() => {
    const formItems: IFormItemType[] = [
      {
        id: 'relationshipWithTAWALId',
        label: t('dsr.form.relationshipWithTAWALId.label'),
        placeholder: t('dsr.form.select.placeholder'),
        type: 'select',
        required: true,
        value: formik.values.relationshipWithTAWALId,
        isTouched: formik.touched.relationshipWithTAWALId,
        isValid: !formik.errors.relationshipWithTAWALId,
        errorFeedback: formik.errors.relationshipWithTAWALId,
        onChange: (value) => {
          setIsTawalRelationOther(value === 'OTHER');
          formik.setFieldValue('relationshipWithTAWALId', value);
        },
        onBlur: () => formik.setFieldTouched('relationshipWithTAWALId', true),
        options: lookups.RELATIONSHIP_WITH_TAWAL,
        note: t('dsr.form.dsrRelationshipId.note'),
      },
    ];
    if (isTawalRelationOther) {
      formItems.push({
        id: 'otherRelationshipWithTAWAL',
        label: t('dsr.form.other.label'),
        placeholder: t('dsr.form.other.label'),
        type: 'text',
        required: true,
        value: formik.values.otherRelationshipWithTAWAL,
        isTouched: formik.touched.otherRelationshipWithTAWAL,
        isValid: !formik.errors.otherRelationshipWithTAWAL,
        errorFeedback: formik.errors.otherRelationshipWithTAWAL,
        maxLength: 25,
        onChange: (value) => formik.setFieldValue('otherRelationshipWithTAWAL', value),
        onBlur: () => formik.setFieldTouched('otherRelationshipWithTAWAL', true),
      });
    }
    return formItems;
  }, [formik, isTawalRelationOther, lookups.RELATIONSHIP_WITH_TAWAL, t]);
  const requestTypeFormItems: IFormItemType[] = useMemo(() => {
    const formItems: IFormItemType[] = [
      {
        id: 'requestTypeId',
        label: t('dsr.form.requestTypeId.label'),
        placeholder: t('dsr.form.select.placeholder'),
        type: 'select',
        required: true,
        value: formik.values.requestTypeId,
        isTouched: formik.touched.requestTypeId,
        isValid: !formik.errors.requestTypeId,
        errorFeedback: formik.errors.requestTypeId,
        onChange: (value) => {
          setRequestType(value as string);
          formik.setFieldValue('requestTypeId', value);
        },
        onBlur: () => formik.setFieldTouched('requestTypeId', true),
        options: lookups.REQUEST_TYPE,
      },
    ];
    if (requestType === 'INFORM') {
      formItems.push({
        id: 'informDetails',
        label: t('dsr.form.informDetails.label'),
        placeholder: t('dsr.form.textarea.placeholder'),
        type: 'textarea',
        required: true,
        value: formik.values.informDetails,
        isTouched: formik.touched.informDetails,
        isValid: !formik.errors.informDetails,
        errorFeedback: formik.errors.informDetails,
        maxLength: 120,
        onChange: (value) => formik.setFieldValue('informDetails', value),
        onBlur: () => formik.setFieldTouched('informDetails', true),
        note: t('dsr.form.informDetails.note'),
      });
    } else if (requestType === 'ACCESS') {
      formItems.push({
        id: 'accessDetails',
        label: t('dsr.form.accessDetails.label'),
        placeholder: t('dsr.form.textarea.placeholder'),
        type: 'textarea',
        required: true,
        value: formik.values.accessDetails,
        isTouched: formik.touched.accessDetails,
        isValid: !formik.errors.accessDetails,
        errorFeedback: formik.errors.accessDetails,
        maxLength: 120,
        onChange: (value) => formik.setFieldValue('accessDetails', value),
        onBlur: () => formik.setFieldTouched('accessDetails', true),
        note: t('dsr.form.accessDetails.note'),
      });
    } else if (requestType === 'OBTAIN_COPY') {
      formItems.push({
        id: 'obtainCopyDetails',
        label: t('dsr.form.obtainCopyDetails.label'),
        placeholder: t('dsr.form.textarea.placeholder'),
        type: 'textarea',
        required: true,
        value: formik.values.obtainCopyDetails,
        isTouched: formik.touched.obtainCopyDetails,
        isValid: !formik.errors.obtainCopyDetails,
        errorFeedback: formik.errors.obtainCopyDetails,
        maxLength: 120,
        onChange: (value) => formik.setFieldValue('obtainCopyDetails', value),
        onBlur: () => formik.setFieldTouched('obtainCopyDetails', true),
        note: t('dsr.form.obtainCopyDetails.note'),
      });
      formItems.push({
        id: 'receiveInfoId',
        label: t('dsr.form.receiveInfoId.label'),
        placeholder: t('dsr.form.select.placeholder'),
        type: 'select',
        required: true,
        value: formik.values.receiveInfoId,
        isTouched: formik.touched.receiveInfoId,
        isValid: !formik.errors.receiveInfoId,
        errorFeedback: formik.errors.receiveInfoId,
        onChange: (value) => {
          formik.setFieldValue('receiveInfoId', value);
        },
        onBlur: () => formik.setFieldTouched('receiveInfoId', true),
        options: lookups.RECEIVE_INFO,
        note: t('dsr.form.receiveInfoId.note'),
      });
    } else if (requestType === 'CORRECTION') {
      formItems.push({
        id: 'correctionDetails',
        label: t('dsr.form.correctionDetails.label'),
        placeholder: t('dsr.form.textarea.placeholder'),
        type: 'textarea',
        required: true,
        value: formik.values.correctionDetails,
        isTouched: formik.touched.correctionDetails,
        isValid: !formik.errors.correctionDetails,
        errorFeedback: formik.errors.correctionDetails,
        maxLength: 120,
        onChange: (value) => formik.setFieldValue('correctionDetails', value),
        onBlur: () => formik.setFieldTouched('correctionDetails', true),
        note: t('dsr.form.correctionDetails.note'),
      });
    } else if (requestType === 'DESTRUCTION') {
      formItems.push({
        id: 'destructionDetails',
        label: t('dsr.form.destructionDetails.label'),
        placeholder: t('dsr.form.textarea.placeholder'),
        type: 'textarea',
        required: true,
        value: formik.values.destructionDetails,
        isTouched: formik.touched.destructionDetails,
        isValid: !formik.errors.destructionDetails,
        errorFeedback: formik.errors.destructionDetails,
        maxLength: 120,
        onChange: (value) => formik.setFieldValue('destructionDetails', value),
        onBlur: () => formik.setFieldTouched('destructionDetails', true),
        note: t('dsr.form.destructionDetails.note'),
      });
    } else if (requestType === 'WITHDRAW') {
      formItems.push({
        id: 'withdrawConsentDetails',
        label: t('dsr.form.withdrawConsentDetails.label'),
        placeholder: t('dsr.form.textarea.placeholder'),
        type: 'textarea',
        required: true,
        value: formik.values.withdrawConsentDetails,
        isTouched: formik.touched.withdrawConsentDetails,
        isValid: !formik.errors.withdrawConsentDetails,
        errorFeedback: formik.errors.withdrawConsentDetails,
        maxLength: 120,
        onChange: (value) => formik.setFieldValue('withdrawConsentDetails', value),
        onBlur: () => formik.setFieldTouched('withdrawConsentDetails', true),
        note: t('dsr.form.withdrawConsentDetails.note'),
      });
    }
    return formItems;
  }, [formik, lookups.RECEIVE_INFO, lookups.REQUEST_TYPE, requestType, t]);
  const verificationFormItems: IFormItemType[] = useMemo(() => {
    const formItems: IFormItemType[] = [
      {
        id: 'userVerificationId',
        label: t('dsr.form.userVerificationId.label'),
        placeholder: t('dsr.form.select.placeholder'),
        type: 'select',
        required: true,
        value: formik.values.userVerificationId,
        isTouched: formik.touched.userVerificationId,
        isValid: !formik.errors.userVerificationId,
        errorFeedback: formik.errors.userVerificationId,
        onChange: (value) => {
          setVerificationType(value as string);
          formik.setFieldValue('userVerificationId', value);
        },
        onBlur: () => formik.setFieldTouched('userVerificationId', true),
        options: lookups.USER_VERIFICATION,
        note: t('dsr.form.userVerificationId.note'),
      },
    ];
    if (verificationType === 'EMAIL') {
      formItems.push({
        id: 'verificationEmail',
        label: t('dsr.form.verificationEmail.label'),
        placeholder: t('dsr.form.email.placeholder'),
        type: 'text',
        required: true,
        value: formik.values.verificationEmail,
        isTouched: formik.touched.verificationEmail,
        isValid: !formik.errors.verificationEmail,
        errorFeedback: formik.errors.verificationEmail,
        maxLength: 40,
        onChange: (value) => formik.setFieldValue('verificationEmail', value),
        onBlur: () => formik.setFieldTouched('verificationEmail', true),
      });
    } else if (verificationType === 'PHONE') {
      formItems.push({
        id: 'verificationPhoneNumber',
        label: t('dsr.form.verificationPhoneNumber.label'),
        placeholder: '05xxxxxxx',
        type: 'number',
        required: true,
        value: formik.values.verificationPhoneNumber,
        isTouched: formik.touched.verificationPhoneNumber,
        isValid: !formik.errors.verificationPhoneNumber,
        errorFeedback: formik.errors.verificationPhoneNumber,
        maxLength: 10,
        onChange: (value) => formik.setFieldValue('verificationPhoneNumber', value),
        onBlur: () => formik.setFieldTouched('verificationPhoneNumber', true),
      });
    }
    return formItems;
  }, [formik, verificationType, lookups.USER_VERIFICATION, t]);

  return (
    <FormContainer error={error}>
      <SubFormSection items={baseFormItems} />
      {isMyRequest !== undefined && (
        <>
          <SubFormSection items={userDetailsFormItems} title={t('dsr.form.userDetails.title')} />
          {isMyRequest && (
            <SubFormSection
              items={relationFormItems}
              title={t('dsr.form.relationWithTawal.title')}
            />
          )}
          <SubFormSection items={requestTypeFormItems} withDivider />
          <SubFormSection items={verificationFormItems} withDivider />
          <Form.Group className="w-100 pb-3 px-3 d-flex flex-column justify-content-start align-items-start">
            <Form.Control
              as="div"
              className="border-0 w-auto p-0"
              isInvalid={!!formik.errors.recaptchaToken && formik.touched.recaptchaToken}
            >
              <ReCAPTCHA
                ref={recaptchaRef as React.LegacyRef<ReCAPTCHA>}
                sitekey="6LdRwy4qAAAAAIN7oQMsZevTuDmW2m302maO2hwf"
                onChange={(token) => {
                  formik.setFieldValue('recaptchaToken', token ?? '');
                  formik.validateField('recaptchaToken');
                }}
              />
            </Form.Control>
            {formik.errors.recaptchaToken && (
              <CustomInputFeedback errorFeedback={formik.errors.recaptchaToken} />
            )}
          </Form.Group>

          <TermsOfUse />

          <Form.Group className="px-3">
            <Form.Control as="div" className="border-0 w-auto p-0 d-flex gap-2">
              <Form.Check
                type="checkbox"
                onChange={() => {
                  formik.setFieldValue('termsOfUse', !formik.values.termsOfUse);
                  formik.setFieldError('termsOfUse', undefined);
                }}
                required={true}
                name="termsOfUse"
              />
              <Form.Label htmlFor="termsOfUse">{t('request.termsOfUse.label')}</Form.Label>
            </Form.Control>
            {formik.errors.termsOfUse && (
              <CustomInputFeedback errorFeedback={formik.errors.termsOfUse} />
            )}
          </Form.Group>

          <Form.Group className="px-3">
            <Form.Control as="div" className="border-0 w-auto p-0 d-flex gap-2">
              <Form.Check
                type="checkbox"
                name="privacyNotice"
                required={true}
                onChange={() => {
                  formik.setFieldValue('privacyNotice', !formik.values.privacyNotice);
                  formik.setFieldError('privacyNotice', undefined);
                }}
              />
              <Form.Label htmlFor="privacyNotice">
                {t('request.privacyNotice.label')}{' '}
                <a href={t('request.privacyNotice.link')} target="_blank" rel="noreferrer">
                  {t('request.privacyNotice')}
                </a>
              </Form.Label>
            </Form.Control>
            {formik.errors.privacyNotice && (
              <CustomInputFeedback errorFeedback={formik.errors.privacyNotice} />
            )}
          </Form.Group>

          <div className="w-100 pt-3 pb-5 d-flex justify-content-center align-items-center">
            <CustomButton
              variant="primary"
              text={t('submit')}
              disabled={isLoading || ipMutation.isLoading}
              suffixIcon={ArrowIcon}
              onClick={() => {
                formik.handleSubmit();
              }}
            />
          </div>
        </>
      )}
    </FormContainer>
  );
};
